import { Button, DatePicker, Form, Input, Modal, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { loadSingleStaff } from "../../../redux/rtk/features/user/userSlice";

import BtnEditSvg from "../Button/btnEditSvg";
import { addDepartmentHistory, getDepartments } from "../../department/departmentApis";

const DepartmentEditSinglePopup = ({ data, setLoading }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [EffectedFrom, setEffectedFrom] = useState(
        data?.EffectedFrom ? dayjs(data.EffectedFrom).format("YYYY-MM-DD") : null
    );
    const [EffectedTo, setEffectedTo] = useState(
        data?.EffectedTo ? dayjs(data.EffectedTo).format("YYYY-MM-DD") : null
    );
    const [loader, setLoader] = useState(false);
    const [designationComment, setDesignationComment] = useState("");

    const { id } = useParams();
    const employeeId = Number(id) || Number(data?.EmployeeId) || 0;
    const dispatch = useDispatch();
    const designations = useSelector((state) => state.designations.list);
    const [DepartmentId, setDepartmentId] = useState(
        data?.designation?.DepartmentId || data?.DepartmentId
    );

    const [initialValues, setInitialValues] = useState({
        DepartmentId: data?.DepartmentId || "",
        EffectedFrom: data?.EffectedFrom ? dayjs(data.EffectedFrom) : null,
        EffectedTo: data?.EffectedTo ? dayjs(data.EffectedTo) : null,
    });

    const [departments, setDepartments] = useState([]);
    
    useEffect(() => {
      getDepartments()
        .then((d) => {
          // console.log("Fetched departments:", d);
          setDepartments(d); // correctly set the department list
        })
        // .catch((error) => // console.log("Error fetching departments:", error));
    }, []);

    useEffect(() => {
        if (data) {
            setInitialValues({
                DepartmentId: data.DepartmentId || "",
                EffectedFrom: data.EffectedFrom ? dayjs(data.EffectedFrom) : null,
                EffectedTo: data.EffectedTo ? dayjs(data.EffectedTo) : null,
            });
            setDepartmentId(data.DepartmentId || data?.designation?.DepartmentId);
            setEffectedFrom(data.EffectedFrom ? dayjs(data.EffectedFrom).format("YYYY-MM-DD") : null);
            setEffectedTo(data.EffectedTo ? dayjs(data.EffectedTo).format("YYYY-MM-DD") : null);
        }
    }, [data]);

    const onFinish = async (values) => {
        if (!employeeId) {
            toast.error("Employee ID is missing. Please open the employee profile again.");
            setLoader(false);
            setLoading(false);
            return;
        }

        setLoading(true);
        setLoader(true);

        const infodata = {
            DepartmentHistoryId: data.DepartmentHistoryId || 0,
            EmployeeId: employeeId,
            DepartmentId: Number(DepartmentId),
            EffectedFrom: EffectedFrom,
            EffectedTo: EffectedTo,
            DataStatus: 1,
        };

        const resp = await addDepartmentHistory(infodata);

        if (resp.message === "success") {
            setLoader(false);
            dispatch(loadSingleStaff(employeeId));
            setInitialValues({});
            setDepartmentId("");
            setDesignationComment("");
            setEffectedFrom(null);
            setEffectedTo(null);
            setIsModalOpen(false);
            setLoading(false);
        } else {
            setLoading(false);
            setLoader(false);
            toast.error(resp.message || "Failed to update designation");
        }
    };

    const onFinishFailed = (errorInfo) => {
        toast.warning("Failed at updating designation");
        setLoader(false);
        setLoading(false);
    };

    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleOk = () => {
        setDepartmentId("");
        setDesignationComment("");
        setEffectedFrom(null);
        setEffectedTo(null);
        setIsModalOpen(false);
        setLoader(false);
        setLoading(false);
    };

    const handleCancel = () => {
        setDepartmentId("");
        setDesignationComment("");
        setEffectedFrom(null);
        setEffectedTo(null);
        setIsModalOpen(false);
        setLoader(false);
        setLoading(false);
    };

    return (
        <>
            <button onClick={showModal} className="mr-2">
                <BtnEditSvg size={20} />
            </button>
            <Modal
                title={`Edit Department`}
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
            >
                <Form
                    style={{ marginBottom: "100px" }}
                    eventKey="design-form"
                    initialValues={initialValues}
                    name="basic"
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 16 }}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                >
                    <div>
                        <Form.Item
                            style={{ marginBottom: "10px" }}
                            label="Department"
                            name="DepartmentId"
                            rules={[
                                {
                                    required: true,
                                    message: "Please input your Designation!",
                                },
                            ]}
                        >
                            <Select
                                placeholder="Select Department"
                                onChange={(value) => setDepartmentId(value)}
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {departments?.map((item) => (
                                    <Select.Option key={item.DepartmentId} value={item.DepartmentId}>
                                        {item.DepartmentName || ""}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            style={{ marginBottom: "10px" }}
                            label="Effected From"
                            name="EffectedFrom"
                            rules={[
                                {
                                    required: true,
                                    message: "Please input your Effected From!",
                                },
                            ]}
                        >
                            <DatePicker
                                name="EffectedFrom"
                                format="YYYY-MM-DD"
                                value={EffectedFrom ? dayjs(EffectedFrom, "YYYY-MM-DD") : null}
                                onChange={(date) => setEffectedFrom(date ? date.format("YYYY-MM-DD") : null)}
                            />
                        </Form.Item>

                        <Form.Item
                            style={{ marginBottom: "10px" }}
                            label="Effected To"
                            name="EffectedTo"
                        >
                            <DatePicker
                                name="EffectedTo"
                                format="YYYY-MM-DD"
                                value={EffectedTo ? dayjs(EffectedTo, "YYYY-MM-DD") : null}
                                onChange={(date) => setEffectedTo(date ? date.format("YYYY-MM-DD") : null)}
                            />
                        </Form.Item>

                        <Form.Item
                            style={{ marginBottom: "20px" }}
                            wrapperCol={{ offset: 6, span: 12 }}
                        >
                            <Button
                                onClick={() => setLoader(true)}
                                type="primary"
                                size="small"
                                htmlType="submit"
                                block
                                loading={loader}
                            >
                                Update Now
                            </Button>
                        </Form.Item>
                    </div>
                </Form>
            </Modal>
        </>
    );
};

export default DepartmentEditSinglePopup;